import { Howler } from "howler";
import { makeAutoObservable } from "mobx";
import { parseJwt, isLocalStorageAvailable } from "../utils";
import { create, all } from "mathjs";
import { getSkin, getTranslations } from "../api";
// import {gameComponentsByGameId} from "../constants/gameComponentsByGameId";
import { plinkoSkinParser } from "../games/plinko/plinkoUtils/plinkoSkinParser";
import { limboSkinParser } from "../games/limbo/limboUtils/limboSkin";
import { wheelSkinParser } from "../games/wheel/wheelUtils/wheelSkinParser";
import { chickenSkinParser } from "../games/chicken/chickenUtils/chickenSkin";
import { minesSkinParser } from "../games/mines/minesUtils/minesSkin";
import { halloweenMinesSkinParser } from "../games/halloween-mines/utils/minesSkin";
import { GAME_IDS } from "../constants/gameIds";
import { rouletteSkinParser } from "../games/roulette/utils/skin";
import { balloonSkinParser } from "../games/balloon/utils/balloonSkin";
import { towerCrashSkinParser } from "../games/tower-crash/utils/towerCrashSkin";
import { camelCrashSkinParser } from "../games/camel-crash/utils/skin";
import diceSkinParser from "../games/dice/utils/skin";

const math = create(all, {});

class MainStore {
  betAmount = 5;
  minBet = 1;
  maxBet = 1;
  maxWin = 0;
  balance = 100000.0;
  gameId = 11;
  accessToken = null;
  partnerId = null;
  sessionDurationSeconds = 500;
  currency = "FUN";
  parsedJwt = null;
  isSoundOff = isLocalStorageAvailable() ? localStorage.getItem("isSoundOff") === "true" : false;
  isSessionExpired = false;
  languageCode = null;
  fastMode = false;
  infoSectionOpened = false;
  fairnessSectionOpened = false;
  errorMsg = null;
  isShowReconnectingModal = false;
  translations = {};
  skin = {};
  oddsHistory = [];

  constructor() {
    makeAutoObservable(this);
    this.setPartnerIdByUrl();
    this.setLanguageCodeByUrl();
    this.setGameId();
    this.setSkinFromBackend();
    this.setTranslationsFromBackend();
  }

  addOddsHistoryItem(data) {
    this.oddsHistory.push(data);
    if (this.oddsHistory.length > 30) {
      this.oddsHistory.shift();
    }
  }

  getExtendedGameId() {
    switch (this.gameId) {
      case GAME_IDS.CHICKEN_BRANDED:
        return GAME_IDS.CHICKEN;
      case GAME_IDS.CHICKEN_CHRISTMAS:
        return GAME_IDS.CHICKEN;
      case GAME_IDS.TWIST_CLASSIC:
        return GAME_IDS.TWIST;
      case GAME_IDS.CANDY_TOWER:
        return GAME_IDS.TOWER_CRASH;
      default:
        return this.gameId;
    }
  }

  setSkinFromBackend() {
    getSkin(this.getExtendedGameId(), this.getSkinName()).then((res) => {
      this.setSkinByGameSkinParser(res.data);
    });
  }

  setSkinByGameSkinParser(skinData) {
    switch (this.gameId) {
      case GAME_IDS.PLINKO:
        this.setSkin(plinkoSkinParser(skinData));
        break;
      case GAME_IDS.LIMBO:
        this.setSkin(limboSkinParser(skinData));
        break;
      case GAME_IDS.WHEEL:
        this.setSkin(wheelSkinParser(skinData));
        break;
      case GAME_IDS.CHICKEN:
        this.setSkin(chickenSkinParser(skinData));
        break;
      case GAME_IDS.CHICKEN_BRANDED:
        this.setSkin(chickenSkinParser(skinData));
        break;
      case GAME_IDS.MINES:
        this.setSkin(minesSkinParser(skinData));
        break;
      case GAME_IDS.HALLOWEEN_MINES:
        this.setSkin(halloweenMinesSkinParser(skinData));
        break;
      case GAME_IDS.ROULETTE:
        this.setSkin(rouletteSkinParser(skinData));
        break;
      case GAME_IDS.BALLOON:
        this.setSkin(balloonSkinParser(skinData));
        break;
      case GAME_IDS.TOWER_CRASH:
        this.setSkin(towerCrashSkinParser(skinData));
        break;
      case GAME_IDS.CAMEL_CRASH:
        this.setSkin(camelCrashSkinParser(skinData));
        break;
      case GAME_IDS.DICE:
        this.setSkin(diceSkinParser(skinData));
        break;
      default:
        break;
    }
  }

  setSkin(skinObj) {
    this.skin = skinObj;
  }

  setTranslationsFromBackend() {
    getTranslations(this.getExtendedGameId(), this.languageCode).then((res) => this.setTranslations(res.data));
  }

  setTranslations(obj) {
    this.translations = obj;
  }

  setBalance(newBalance) {
    this.balance = newBalance;
    this.balance = this.balance.toFixed(2);
  }

  addBalance(value) {
    this.balance = math.add(this.balance, value);
    this.balance = this.balance.toFixed(2);
  }

  subtractBalance(value) {
    this.balance = math.subtract(this.balance, value);
    this.balance = this.balance.toFixed(2);
  }

  setIsShowReconnectingModal(bool) {
    this.isShowReconnectingModal = bool;
  }

  setErrorMsg(data) {
    this.errorMsg = data;
  }

  setInfoSectionOpened(bool) {
    this.infoSectionOpened = bool;
  }

  setFairnessSectionOpened(bool) {
    this.fairnessSectionOpened = bool;
  }

  soundToggle() {
    this.isSoundOff = !this.isSoundOff;
    isLocalStorageAvailable() && localStorage.setItem("isSoundOff", this.isSoundOff.toString());
    Howler.mute(this.isSoundOff);
  }

  setFastMode(bool) {
    this.fastMode = bool;
  }

  setMinBet(minBet) {
    this.minBet = minBet;
    this.betAmount = Math.max(this.betAmount, this.minBet);
  }

  setMaxBet(maxBet) {
    this.maxBet = maxBet;
    this.betAmount = Math.min(this.betAmount, this.maxBet);
  }

  setMaxWin(maxWin) {
    this.maxWin = maxWin;
  }

  setBetAmount(amount) {
    this.betAmount = amount;
  }

  setPartnerId(id) {
    this.partnerId = id;
  }

  setLanguageCodeByUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    this.languageCode = urlParams.get("languageCode");
  }

  setPartnerIdByUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const partnerId = urlParams.get("partnerId");
    this.setPartnerId(partnerId * 1);
  }

  isDemo() {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get("mode");
    return mode === "fun";
  }

  getSkinName() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("skin");
  }

  setGameId() {
    const urlParams = new URLSearchParams(window.location.search);
    console.log("gameId");
    console.log(urlParams.get("gameId") * 1);
    this.gameId = urlParams.get("gameId") * 1;
  }

  setAccessToken(token) {
    this.accessToken = token;
    this.parsedJwt = parseJwt(token);
    this.currency = this.isDemo() ? "FUN" : this.parsedJwt.currency;
  }
}

export const mainStore = new MainStore();
